import React from "react";
import { Badge, Button } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FormattedMessage, useIntl } from "react-intl";
import { ExperimentEntity, ExperimentStatus } from "../domain/experiment.entity";
import type { Locale } from "date-fns";
import { dateFnsLocaleFor, formatShortDate } from "../../../../lib/dateUtils";
import { useHotelTimeZone } from "../application/hooks/useHotelTimeZone";
import { messages } from "../experiment.messages";

export interface ResultsHeaderProps {
  experiment: ExperimentEntity;
  // Backend-authoritative (ExperimentResultsSchema.activeDays, US-001), sourced from the shell's
  // own useExperimentResults call; undefined until it resolves — see Business Rule 6.
  activeDays: number | undefined;
  /** `ExperimentResults.minDays` — the exposure minimum the time-line block measures against.
   *  `0` (unset on the wire) and `undefined` (query unresolved) both drop the denominator rather
   *  than rendering "8 de 0 días". */
  minDays?: number;
  // isChangingStatus from useExperimentMutations() — disables all three buttons while a
  // changeStatus mutation is in flight.
  busy: boolean;
  onPauseClick: () => void;
  onResumeClick: () => void;
  onFinishClick: () => void;
  /** Returns to the experiment list. Rendered as a small text link above the title, replacing the
   *  page shell's old full-size outline button (and its duplicate at the foot of the page). */
  onBackClick: () => void;
  /**
   * `ExperimentResults.dataUntil` — the last fully-loaded hotel-local day, ISO "YYYY-MM-DD".
   * `null` (an empty string on the wire) means the backend does not know, and `undefined` means the
   * results query has not resolved; both hide the entry rather than render "Datos hasta ".
   */
  dataUntil?: string | null;
  /** `ExperimentResults.source` — "warehouse" | "witbooking-api" | "fake". */
  source?: string | null;
}

/** Provenance / configured-split chips. Neutral styling — these state a fact about the data, they
 *  are not a status the user has to act on. */
function MetaChip({ children, testId }: React.PropsWithChildren<{ testId: string }>) {
  return (
    <Badge variant="light" className="font-weight-normal text-muted border" data-testid={testId}>
      {children}
    </Badge>
  );
}

/** The scenario chips carry the same A-orange / B-navy coding as the comparison cards and the
 *  chart legend, so a name can be traced across the screen by colour — never by colour ALONE: the
 *  "A ·" / "B ·" prefix says the same thing in text. */
const ScenarioChip = styled.span<{ $side: "A" | "B" }>`
  color: ${({ theme, $side }) =>
    $side === "A" ? theme.orangeBackground : theme.darkBlueBackground};
  border: 1px solid currentColor;
  border-radius: 0.75rem;
  padding: 0.1rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.4;
`;

/** The header's two halves. On a narrow viewport the right-hand time-line block stacks under the
 *  left one instead of squeezing it. */
const HeaderLayout = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;

  @media (max-width: 991px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const TimelineBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.75rem;
  flex-shrink: 0;

  @media (max-width: 991px) {
    align-items: flex-start;
    width: 100%;
  }
`;

const TimelineGrid = styled.dl`
  display: flex;
  gap: 1.5rem;
  margin: 0;
  text-align: right;

  @media (max-width: 991px) {
    text-align: left;
    flex-wrap: wrap;
    gap: 1rem 1.5rem;
  }
`;

const TimelineLabel = styled.dt`
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.regularTextColor};
  margin: 0;
`;

const TimelineValue = styled.dd`
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.3;
`;

/** A text link, not a pill: going back is the least important thing on this screen, and the old
 *  outline button competed with the lifecycle actions for attention. */
const BackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.regularTextColor};

  &:hover {
    color: ${({ theme }) => theme.textColor};
    text-decoration: underline;
  }
`;

function getSourceMessage(source: string) {
  switch (source) {
    case "warehouse":
      return { descriptor: messages.results.sourceWarehouse, values: {} };
    case "witbooking-api":
      return { descriptor: messages.results.sourceApi, values: {} };
    case "fake":
      return { descriptor: messages.results.sourceFake, values: {} };
    default:
      // A source this build has not been taught about is still named, verbatim.
      return { descriptor: messages.results.sourceUnknown, values: { source } };
  }
}

const statusBadgeColors: Record<ExperimentStatus, "secondary" | "success" | "warning" | "info"> = {
  [ExperimentStatus.EXPERIMENT_STATUS_UNSPECIFIED]: "secondary",
  [ExperimentStatus.EXPERIMENT_STATUS_DRAFT]: "secondary",
  [ExperimentStatus.EXPERIMENT_STATUS_RUNNING]: "success",
  [ExperimentStatus.EXPERIMENT_STATUS_PAUSED]: "warning",
  [ExperimentStatus.EXPERIMENT_STATUS_ARCHIVED]: "info",
};

function getStatusLabel(status: ExperimentStatus, intl: ReturnType<typeof useIntl>): string {
  switch (status) {
    case ExperimentStatus.EXPERIMENT_STATUS_DRAFT:
      return intl.formatMessage(messages.status.draft);
    case ExperimentStatus.EXPERIMENT_STATUS_RUNNING:
      return intl.formatMessage(messages.status.running);
    case ExperimentStatus.EXPERIMENT_STATUS_PAUSED:
      return intl.formatMessage(messages.status.paused);
    case ExperimentStatus.EXPERIMENT_STATUS_ARCHIVED:
      return intl.formatMessage(messages.status.archived);
    default:
      return intl.formatMessage(messages.status.unspecified);
  }
}

/**
 * `dataUntil` is a calendar date ("YYYY-MM-DD"), not an instant: the backend has already resolved
 * which hotel-local day it means. It is read as UTC midnight and formatted in UTC so the day
 * printed is exactly the one named, whatever zone the browser runs in, and it is rendered in the
 * same short style as the start date instead of as a raw ISO string. A value that is not a plain
 * calendar date is shown verbatim rather than as "Invalid Date".
 */
function formatCalendarDate(isoDateOnly: string, locale: Locale): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDateOnly);
  if (!match) return isoDateOnly;
  const utcMidnight = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return formatShortDate(utcMidnight, "UTC", locale);
}

function TimelineEntry({
  label,
  testId,
  children,
  hint,
}: React.PropsWithChildren<{ label: React.ReactNode; testId: string; hint?: React.ReactNode }>) {
  return (
    <div data-testid={testId}>
      <TimelineLabel>{label}</TimelineLabel>
      <TimelineValue>{children}</TimelineValue>
      {hint && (
        <div className="text-muted" style={{ fontSize: "0.7rem" }}>
          {hint}
        </div>
      )}
    </div>
  );
}

export function ResultsHeader({
  experiment,
  activeDays,
  minDays,
  busy,
  onPauseClick,
  onResumeClick,
  onFinishClick,
  onBackClick,
  dataUntil,
  source,
}: ResultsHeaderProps) {
  const intl = useIntl();
  const hotelTimeZone = useHotelTimeZone();
  const { status, ticker, description, variants } = experiment;

  const sourceMessage = source ? getSourceMessage(source) : null;

  const showPause = status === ExperimentStatus.EXPERIMENT_STATUS_RUNNING;
  const showResume = status === ExperimentStatus.EXPERIMENT_STATUS_PAUSED;
  const showFinishTest =
    status !== ExperimentStatus.EXPERIMENT_STATUS_ARCHIVED &&
    status !== ExperimentStatus.EXPERIMENT_STATUS_DRAFT &&
    status !== ExperimentStatus.EXPERIMENT_STATUS_UNSPECIFIED;

  const hasMinDays = typeof minDays === "number" && minDays > 0;
  // Dates on this header are hotel-local days, rendered in the UI's own language — not in the
  // browser's zone, and not in a hardcoded Spanish.
  const dateFnsLocale = dateFnsLocaleFor(intl.locale);

  return (
    <div>
      <div className="mb-2">
        <BackLink type="button" onClick={onBackClick} data-testid="results-back-link">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          <FormattedMessage {...messages.results.backToList} />
        </BackLink>
      </div>

      <HeaderLayout>
        <div>
          <div className="d-flex align-items-center flex-wrap gap-3">
            <h2 className="mb-0">{ticker}</h2>
            <Badge variant={statusBadgeColors[status]}>{getStatusLabel(status, intl)}</Badge>
          </div>

          {description && <p className="text-muted mb-0 mt-1">{description}</p>}

          <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
            <ScenarioChip $side="A" data-testid="scenario-chip-A">
              <FormattedMessage
                {...messages.results.header.scenarioChip}
                values={{ side: "A", name: variants[0].ticker }}
              />
            </ScenarioChip>
            <ScenarioChip $side="B" data-testid="scenario-chip-B">
              <FormattedMessage
                {...messages.results.header.scenarioChip}
                values={{ side: "B", name: variants[1].ticker }}
              />
            </ScenarioChip>
            {sourceMessage && (
              <MetaChip testId="source-chip">
                <FormattedMessage {...sourceMessage.descriptor} values={sourceMessage.values} />
              </MetaChip>
            )}
          </div>
        </div>

        <TimelineBlock data-testid="results-timeline">
          <TimelineGrid>
            <TimelineEntry
              testId="timeline-start"
              label={<FormattedMessage {...messages.results.header.timelineStart} />}>
              {formatShortDate(experiment.startedAt, hotelTimeZone, dateFnsLocale)}
            </TimelineEntry>

            {/* Dropped entirely, rather than rendered as "undefined días", while the results
                query has not resolved. */}
            {activeDays !== undefined && (
              <TimelineEntry
                testId="timeline-active-days"
                label={<FormattedMessage {...messages.results.header.timelineRunning} />}
                hint={
                  hasMinDays ? (
                    <FormattedMessage
                      {...messages.results.header.timelineMinHint}
                      values={{ minDays }}
                    />
                  ) : undefined
                }>
                {hasMinDays ? (
                  <FormattedMessage
                    {...messages.results.header.timelineDaysValue}
                    values={{ activeDays, minDays }}
                  />
                ) : (
                  <FormattedMessage
                    {...messages.results.header.timelineDaysNoMin}
                    values={{ activeDays }}
                  />
                )}
              </TimelineEntry>
            )}

            {dataUntil && (
              <TimelineEntry
                testId="timeline-data-until"
                label={<FormattedMessage {...messages.results.header.timelineDataUntil} />}>
                {/* `hotelTimeZone` is undefined while the property configuration loads; the day
                    itself is the load-bearing half, so it is shown WITHOUT the zone — and without
                    the separator that used to dangle after it — rather than withheld. */}
                {hotelTimeZone ? (
                  <FormattedMessage
                    {...messages.results.header.timelineDataUntilValue}
                    values={{
                      date: formatCalendarDate(dataUntil, dateFnsLocale),
                      timeZone: hotelTimeZone,
                    }}
                  />
                ) : (
                  <FormattedMessage
                    {...messages.results.header.timelineDataUntilValueNoZone}
                    values={{ date: formatCalendarDate(dataUntil, dateFnsLocale) }}
                  />
                )}
              </TimelineEntry>
            )}
          </TimelineGrid>

          <div className="d-flex gap-2">
            {showPause && (
              <Button variant="outline-secondary" onClick={onPauseClick} disabled={busy}>
                <FormattedMessage {...messages.results.pause} />
              </Button>
            )}
            {showResume && (
              <Button variant="outline-secondary" onClick={onResumeClick} disabled={busy}>
                <FormattedMessage {...messages.results.resume} />
              </Button>
            )}
            {showFinishTest && (
              <Button variant="primary" onClick={onFinishClick} disabled={busy}>
                <FormattedMessage {...messages.results.finishTest} />
              </Button>
            )}
          </div>
        </TimelineBlock>
      </HeaderLayout>
    </div>
  );
}
